import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TuneIcon from "@mui/icons-material/Tune";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LoadSpinner from "../../../utilities/LoadSpinner";
import FormPreviewModal from "./FormPreviewModal";
import AiAssetGeneratorModal from "./AiAssetGeneratorModal";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getRightworkAPI, postRightworkAPI } from "../requests/utilities/requests";
import ReactDOM from "react-dom";
import { setDirectAssignability } from "./DirectAssignabilityToggle";
import ReuseSignalBanner from "./ReuseSignalBanner";
import DiscardChangesConfirm from "./DiscardChangesConfirm";
import FormPublicShareControls from "./FormPublicShareControls";
import { FormFieldListPane } from "./FormFieldListPane";
import { FormFieldEditorPane } from "./FormFieldEditorPane";
import { FormSettingsPane } from "./FormSettingsPane";
import {
  typesForPresentation,
  makeDefaultField,
  hydrateField,
  isManualType,
  hasOptions,
  isDisplayOnlyType,
} from "./formFieldHelpers";
import { validateShowIf, stripPipingFor, danglingPipingRefs } from "./formLogic";
import {
  API_post_learning_form,
  API_get_learning_form_detail,
  API_delete_learning_form,
} from "../../../constants";

const FormPlayerLazy = React.lazy(() =>
  import("../take_training_employee/form_player/FormPlayer")
);

const style = {
  // Center without `transform` — @hello-pangea/dnd's drag clone uses
  // position:fixed, which resolves against a transformed ancestor instead of the
  // viewport, offsetting the drag ghost. `inset:0 + margin:auto` centers the
  // same way without a transform (mirrors QuizEditorModal/SwipeDeckInputsModal).
  position: "absolute",
  inset: 0,
  margin: "auto",
  width: "96%",
  height: "94%",
  maxWidth: "1600px",
  maxHeight: "94vh",
  bgcolor: "background.paper",
  borderRadius: "12px",
  boxShadow: 24,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  border: "none",
};

// Stable serialization of the editor's content fields — used to detect unsaved
// changes so the Share popover can warn that edits won't appear in the public
// link until the form is saved. Fixed key order so the string is comparable.
const snapshotOf = (s) =>
  JSON.stringify({
    name: s.name,
    description: s.description,
    completedByRole: s.completedByRole,
    scoringEnabled: s.scoringEnabled,
    passThreshold: s.passThreshold,
    gateOnPass: s.gateOnPass,
    saveToProfile: s.saveToProfile,
    presentation: s.presentation,
    welcomeScreen: s.welcomeScreen,
    thankYouScreen: s.thankYouScreen,
    fields: s.fields,
  });

// {title, description} or null when both are blank (the backend stores null).
const normalizeScreen = (scr) => {
  if (!scr) return null;
  const title = (scr.title || "").trim();
  const description = (scr.description || "").trim();
  return title || description ? { title, description } : null;
};

// Welcome / thank-you screens used to be form-level settings; they are now
// authored as `info_screen` fields (which also carry an image and can sit
// anywhere in the flow). Fold any stored screens into the field list the first
// time the slide-show editor sees them — a leading screen becomes the welcome,
// a trailing one becomes the ending the player shows after submit. The player
// still renders the stored screens for forms that were never re-saved.
const foldLegacyScreens = (presentation, welcome, thankYou, fields) => {
  if (presentation !== "slides" || (!welcome && !thankYou)) return fields;
  const asField = (screen) =>
    hydrateField({
      type: "info_screen",
      label: screen.title || "",
      help: screen.description || "",
    });
  return [
    ...(welcome ? [asField(welcome)] : []),
    ...fields,
    ...(thankYou ? [asField(thankYou)] : []),
  ];
};

// A section heading has no divider to draw in a slide show — it already renders
// as a full screen of its own — so it IS an info screen there.
//
// This is a one-time migration of forms authored BEFORE info screens existed:
// it runs when the editor opens a form already set to slides, and on an AI
// draft (whose fields the author is reviewing anyway). It deliberately does NOT
// run when an author flips the presentation toggle — those headings are live
// choices in a form that has been a scrolling form until now, and since the
// toggle can't reverse the conversion, flipping it on and off would quietly
// rewrite them for good. Retyping one is a dropdown away.
const retypeHeadingsForSlides = (presentation, fields) =>
  presentation !== "slides"
    ? fields
    : fields.map((f) => (f.type === "heading" ? { ...f, type: "info_screen" } : f));

// The trailing info screen a slide-show form ends on, if any: the player shows
// it after the response is submitted (see FormPlayer). Needs a real question
// before it, or there would be nothing to submit.
const endingFieldId = (presentation, fields) => {
  if (presentation !== "slides" || fields.length < 2) return null;
  const last = fields[fields.length - 1];
  if (last?.type !== "info_screen") return null;
  return fields.slice(0, -1).some((f) => !isDisplayOnlyType(f.type)) ? last.id : null;
};

function FormEditorModal(props) {
  const seed = props.modalDataForm || {};
  // "training" (default): the full editor. "engagement": the Engagement tab's
  // survey editor. Same component; the variant hides who-fills, scoring and
  // grading, profile records, assignability and section usage, defaults new
  // surveys to the slide show, and saves with purpose="engagement" (the server
  // forces those settings off too).
  const isEngagement = props.variant === "engagement";
  // Copy noun: "survey" in the Engagement tab, "form" in Training.
  const noun = isEngagement ? "survey" : "form";
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [completedByRole, setCompletedByRole] = React.useState("employee");
  const [scoringEnabled, setScoringEnabled] = React.useState(false);
  const [passThreshold, setPassThreshold] = React.useState("");
  const [gateOnPass, setGateOnPass] = React.useState(false);
  const [saveToProfile, setSaveToProfile] = React.useState(false);
  // Survey-style presentation ("list" | "slides") + optional screens.
  const [presentation, setPresentation] = React.useState("list");
  const [welcomeScreen, setWelcomeScreen] = React.useState(null);
  const [thankYouScreen, setThankYouScreen] = React.useState(null);
  const [directlyAssignable, setDirectlyAssignable] = React.useState(false);
  // Loaded value, to detect whether the assignability setting changed on Save.
  const originalAssignableRef = React.useRef(false);
  // Snapshot of the content fields as last loaded/saved — compared against the
  // live state to flag unsaved changes for the Share popover. null until seeded.
  const baselineRef = React.useRef(null);
  // Holds a savedForm from an in-place (Share) save whose parent callback was
  // deferred — replayed on close so a rename/edit reaches the embedding section
  // editor even if the user closes with X instead of a closing Save.
  const pendingSavedFormRef = React.useRef(null);
  const [fields, setFields] = React.useState([]);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [aiGenOpen, setAiGenOpen] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  // "Close without saving?" guard (the X sits next to Create / Save).
  const [confirmDiscard, setConfirmDiscard] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  // Reuse signal: how many sections embed this form (null = unknown/create).
  const [sectionUsageCount, setSectionUsageCount] = React.useState(null);
  // Public share links (the "share a link, collect responses" mode): array of
  // {uuid, label, is_enabled, share_token, collect_respondent_info, open_count,
  // submission_count}.
  const [publicShares, setPublicShares] = React.useState([]);
  // Slide-mode preview opens the real FormPlayer instead of the list preview.
  const [playerPreviewOpen, setPlayerPreviewOpen] = React.useState(false);
  // Set when editing an existing form whose detail failed to load. Blocks the
  // editor body + Save so a failed/empty load can't overwrite the form.
  const [loadError, setLoadError] = React.useState(false);
  // Three-pane editor state: which field is open in the center editor, rail
  // hover, the +Add field type menu, and the collapsible settings rail.
  const [selectedFieldIndex, setSelectedFieldIndex] = React.useState(null);
  const [hoveredFieldIndex, setHoveredFieldIndex] = React.useState(null);
  const [addMenuAnchor, setAddMenuAnchor] = React.useState(null);
  const [showSettings, setShowSettings] = React.useState(true);
  // The whole form renders in the center; the rail is an outline that scrolls
  // to + highlights a field. fieldRefs maps field id -> its card element so a
  // rail click (or a new add) can scroll it into view.
  const fieldRefs = React.useRef({});
  const [scrollTargetId, setScrollTargetId] = React.useState(null);
  // Field id whose question input should grab focus next (set on add) so a new
  // field is obviously editable.
  const [focusFieldId, setFocusFieldId] = React.useState(null);

  // Scroll to a field when a rail click / add requests it (after the card is in
  // the DOM). Center clicks set selection without a scroll target, so focusing a
  // visible field doesn't yank the scroll.
  React.useEffect(() => {
    if (!scrollTargetId) return;
    const el = fieldRefs.current[scrollTargetId];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    setScrollTargetId(null);
  }, [scrollTargetId, fields]);

  // Rail selection: highlight and scroll into view. It deliberately does NOT
  // put the caret in the question — a focused label shows its raw
  // {{field:...}} tokens so they can be edited, and merely selecting a card
  // shouldn't trade the readable version for that. Clicking the label text
  // still focuses it, and a freshly added field still autofocuses (its label is
  // empty, so there's nothing to un-resolve).
  const handleSelectFieldFromRail = (idx) => {
    setSelectedFieldIndex(idx);
    const f = fields[idx];
    if (f) setScrollTargetId(f.id);
  };

  const applySeed = (f) => {
    const nextName = f.name || "";
    const nextDescription = f.description || "";
    const nextRole = f.completed_by_role || "employee";
    const nextScoring = !!f.scoring_enabled;
    const nextThreshold =
      f.pass_threshold === null || f.pass_threshold === undefined
        ? ""
        : String(f.pass_threshold);
    const nextGate = !!f.gate_on_pass;
    const nextSaveProfile = !!f.save_to_profile;
    const nextPresentation =
      f.presentation === "slides" || (isEngagement && !f.uuid && !f.presentation)
        ? "slides"
        : "list";
    const seedWelcome = normalizeScreen(f.welcome_screen);
    const seedThankYou = normalizeScreen(f.thank_you_screen);
    const hydrated = (f.fields || []).map(hydrateField);
    const withScreens = foldLegacyScreens(
      nextPresentation,
      seedWelcome,
      seedThankYou,
      hydrated
    );
    // Folded screens now live in the field list, so drop the stored copies (a
    // save writes null). Nothing is folded in list mode, where the stored
    // screens were never shown in the first place.
    const folded = withScreens.length !== hydrated.length;
    // Headings are migrated ONLY alongside a folded screen. A stored
    // welcome_screen / thank_you_screen is the one reliable marker of a form
    // authored before info screens existed — only the old editor could write
    // those. Retyping every slide form on open would instead mean an author who
    // flips a scrolling form to slides, saves, and reopens finds their section
    // dividers quietly turned into info screens.
    const nextFields = folded
      ? retypeHeadingsForSlides(nextPresentation, withScreens)
      : withScreens;
    const nextWelcome = folded ? null : seedWelcome;
    const nextThankYou = folded ? null : seedThankYou;
    setName(nextName);
    setDescription(nextDescription);
    setCompletedByRole(nextRole);
    setScoringEnabled(nextScoring);
    setPassThreshold(nextThreshold);
    setGateOnPass(nextGate);
    setSaveToProfile(nextSaveProfile);
    setPresentation(nextPresentation);
    setWelcomeScreen(nextWelcome);
    setThankYouScreen(nextThankYou);
    setDirectlyAssignable(!!f.is_directly_assignable);
    originalAssignableRef.current = !!f.is_directly_assignable;
    setPublicShares(Array.isArray(f.public_shares) ? f.public_shares : []);
    setFields(nextFields);
    setSelectedFieldIndex(nextFields.length ? 0 : null);
    // Same hydrated array feeds both state and baseline so we start "clean".
    baselineRef.current = snapshotOf({
      name: nextName,
      description: nextDescription,
      completedByRole: nextRole,
      scoringEnabled: nextScoring,
      passThreshold: nextThreshold,
      gateOnPass: nextGate,
      saveToProfile: nextSaveProfile,
      presentation: nextPresentation,
      welcomeScreen: nextWelcome,
      thankYouScreen: nextThankYou,
      fields: nextFields,
    });
  };

  // Seed state when the modal opens. When editing from the library we get just
  // a { uuid }, so load the full detail (fields) from the backend.
  React.useEffect(() => {
    if (!props.open) return;
    const s = props.modalDataForm || {};
    setSectionUsageCount(null);
    setLoadError(false);
    setAiGenOpen(false);
    setShowSettings(true);
    setAddMenuAnchor(null);
    setHoveredFieldIndex(null);
    setFocusFieldId(null);
    pendingSavedFormRef.current = null;
    if (s.uuid && !s.fields) {
      setIsLoading(true);
      (async () => {
        try {
          const res = await getRightworkAPI(API_get_learning_form_detail, {
            company_id: props.homeCompany,
            learning_form_uuid: s.uuid,
            include_section_usage: true,
          });
          const payload =
            res?.status === 200
              ? res.data?.redux?.payload?.learning_form
              : null;
          if (!payload) {
            // Deleted form, permission error, or transient failure. Don't fall
            // back to an empty seed — that would let a Save overwrite the form
            // with no fields. Surface the failure and block the body + Save.
            throw new Error("Form detail not returned");
          }
          applySeed(payload);
          if (payload.section_usage_count != null) {
            setSectionUsageCount(payload.section_usage_count);
          }
        } catch (e) {
          applySeed(s);
          setLoadError(true);
        } finally {
          setIsLoading(false);
        }
      })();
    } else {
      applySeed(s);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  const isEdit = Boolean(seed.uuid);

  // Seed the CONTENT of the form from an AI draft (generate or AI-edit) —
  // never gate_on_pass / save_to_profile / assignability / the uuid, so a
  // draft over an existing form keeps its settings and identity. baselineRef
  // is deliberately left stale: a fresh draft IS unsaved changes.
  const seedFromAiDraft = (draft) => {
    setName(draft.name || "");
    setDescription(draft.description || "");
    // A survey is always self-filled and unscored; the Engagement prompt says
    // so, but the editor must not trust the draft (a manager role would hide
    // the Share controls until the next reload).
    setCompletedByRole(
      !isEngagement && draft.completed_by_role === "manager" ? "manager" : "employee"
    );
    setScoringEnabled(!isEngagement && !!draft.scoring_enabled);
    setPassThreshold(
      isEngagement || draft.pass_threshold === null || draft.pass_threshold === undefined
        ? ""
        : String(draft.pass_threshold)
    );
    // A draft may turn the slide show ON (asking for a survey should give you
    // one), but never off: how an existing form is presented is the author's
    // call, and silently flattening it would strip its screens.
    const nextPresentation = draft.presentation === "slides" ? "slides" : presentation;
    if (nextPresentation !== presentation) {
      setPresentation(nextPresentation);
      // The draft replaces the content these belonged to, and it carries its
      // own opening/closing screens as fields now.
      setWelcomeScreen(null);
      setThankYouScreen(null);
    }
    // Guards the one mismatch "never off" can create: a draft that came back as
    // a list form while the editor stays in slide show.
    const nextFields = retypeHeadingsForSlides(
      nextPresentation,
      (draft.fields || []).map(hydrateField)
    );
    setFields(nextFields);
    setSelectedFieldIndex(nextFields.length ? 0 : null);
  };

  // "Something to edit" for the AI modal: at least one labeled field. A
  // handful of blank just-added rows isn't meaningful content to revise.
  const hasEditableContent = fields.some((f) => (f.label || "").trim());

  // Unsaved-changes flag for the Share popover: live content vs. the last
  // loaded/saved baseline. Only relevant for existing forms (where Share shows).
  const currentSnapshot = () =>
    snapshotOf({
      name,
      description,
      completedByRole,
      scoringEnabled,
      passThreshold,
      gateOnPass,
      saveToProfile,
      presentation,
      welcomeScreen: normalizeScreen(welcomeScreen),
      thankYouScreen: normalizeScreen(thankYouScreen),
      fields,
    });
  const hasUnsavedChanges =
    isEdit && baselineRef.current != null && currentSnapshot() !== baselineRef.current;

  const updateField = (idx, patch) =>
    setFields((prev) =>
      prev.map((f, i) => {
        if (i !== idx) return f;
        const next = { ...f, ...patch };
        // A patch of { show_if: undefined } removes the condition.
        if ("show_if" in patch && !patch.show_if) delete next.show_if;
        return next;
      })
    );

  const removeField = (idx) => {
    setFields((prev) => {
      const removedId = prev[idx]?.id;
      // Drop any condition that pointed at the removed field, and strip its
      // piping tokens from other questions (they'd render as a placeholder).
      return prev
        .filter((_, i) => i !== idx)
        .map((f) => {
          let next = f;
          if (f.show_if?.field === removedId) {
            const { show_if, ...rest } = next;
            next = rest;
          }
          if (removedId && (f.label?.includes(removedId) || f.help?.includes(removedId))) {
            next = {
              ...next,
              label: stripPipingFor(next.label, removedId),
              help: stripPipingFor(next.help, removedId),
            };
          }
          return next;
        });
    });
    // Keep a sensible selection after removal (length AFTER the splice).
    setSelectedFieldIndex((s) => {
      const len = fields.length - 1;
      if (len <= 0) return null;
      if (s == null) return s;
      if (idx === s) return Math.min(idx, len - 1);
      return idx < s ? s - 1 : s;
    });
  };

  // Add a field of the chosen type (from either +Add menu), select it, and
  // scroll it into view in the center stack.
  const handleAddField = (type) => {
    setAddMenuAnchor(null);
    const f = makeDefaultField(type);
    setFields((prev) => [...prev, f]);
    setSelectedFieldIndex(fields.length); // new field lands at the end
    setScrollTargetId(f.id);
    setFocusFieldId(f.id);
  };

  // Drag-reorder in the rail; keep selection pointing at the same logical field
  // (same remap as the quiz/deck editors).
  const handleFieldDragEnd = (result) => {
    if (!result.destination) return;
    const { source, destination } = result;
    if (source.index === destination.index) return;
    setFields((prev) => {
      const next = Array.from(prev);
      const [moved] = next.splice(source.index, 1);
      next.splice(destination.index, 0, moved);
      return next;
    });
    setSelectedFieldIndex((s) => {
      if (s === null) return s;
      if (s === source.index) return destination.index;
      if (source.index < s && destination.index >= s) return s - 1;
      if (source.index > s && destination.index <= s) return s + 1;
      return s;
    });
  };

  // Option editing for single_select fields
  const updateOption = (fieldIdx, optIdx, patch) =>
    setFields((prev) =>
      prev.map((f, i) =>
        i === fieldIdx
          ? {
              ...f,
              options: (f.options || []).map((o, j) =>
                j === optIdx ? { ...o, ...patch } : o
              ),
            }
          : f
      )
    );
  const addOption = (fieldIdx) =>
    setFields((prev) =>
      prev.map((f, i) =>
        i === fieldIdx
          ? { ...f, options: [...(f.options || []), { label: "", points: 0 }] }
          : f
      )
    );
  const removeOption = (fieldIdx, optIdx) =>
    setFields((prev) =>
      prev.map((f, i) =>
        i === fieldIdx
          ? { ...f, options: (f.options || []).filter((_, j) => j !== optIdx) }
          : f
      )
    );

  // Flipping presentation. Turning the slide show ON folds any legacy stored
  // screens into fields so they stay editable (see foldLegacyScreens). Existing
  // fields keep their types — see retypeHeadingsForSlides for why the toggle
  // leaves headings alone.
  const handlePresentationChange = (next) => {
    setPresentation(next);
    if (next !== "slides") return;
    const welcome = normalizeScreen(welcomeScreen);
    const thankYou = normalizeScreen(thankYouScreen);
    if (!welcome && !thankYou) return;
    setFields((prev) => foldLegacyScreens("slides", welcome, thankYou, prev));
    setWelcomeScreen(null);
    setThankYouScreen(null);
  };

  // Switching to a manager-completed form strips manual free-text grading
  // config (the backend rejects graded text on manager forms), so a hidden
  // graded field can't silently block Save.
  const handleCompletedByRoleChange = (role) => {
    setCompletedByRole(role);
    if (role === "manager") {
      setFields((prev) =>
        prev.map((f) => {
          if (isManualType(f.type) && f.scored) {
            const { max_points, answer_key, ...rest } = f;
            return { ...rest, scored: false };
          }
          return f;
        })
      );
    }
  };

  // Highest score a learner could achieve — mirrors the backend so a threshold
  // that no response could reach is caught before saving.
  const maxPossibleScore = React.useMemo(
    () =>
      fields.reduce((sum, f) => {
        if (!f.scored) return sum;
        if (f.type === "scale") {
          const max = Number(f.max);
          return sum + (Number.isFinite(max) ? max : 0);
        }
        if (f.type === "single_select") {
          const points = (f.options || []).map((o) => Number(o.points) || 0);
          return sum + (points.length ? Math.max(...points) : 0);
        }
        if (isManualType(f.type)) {
          const mp = Number(f.max_points);
          return sum + (Number.isFinite(mp) ? mp : 1);
        }
        return sum;
      }, 0),
    [fields]
  );

  // Fields whose show_if is broken: points at a later / missing / heading
  // field, uses an op that doesn't fit the target's type, or a value that no
  // longer exists on the target (renamed/removed option, out-of-range scale).
  const showIfProblems = React.useMemo(() => {
    const out = [];
    fields.forEach((f, idx) => {
      if (!f.show_if?.field) return;
      if (validateShowIf(f.show_if, fields.slice(0, idx))) out.push(f.id);
    });
    return out;
  }, [fields]);
  // Fields whose label/help pipes an answer that doesn't come before them.
  const pipingProblems = React.useMemo(() => {
    const out = [];
    fields.forEach((f, idx) => {
      if (danglingPipingRefs(f, fields.slice(0, idx)).length) out.push(f.id);
    });
    return out;
  }, [fields]);

  const endingId = endingFieldId(presentation, fields);
  const hasScoredField = fields.some((f) => f.scored);
  const thresholdNum = passThreshold === "" ? null : Number(passThreshold);
  // A pass threshold needs a reachable score, or every learner fails forever.
  const scoringConfigValid =
    !scoringEnabled ||
    thresholdNum === null ||
    (hasScoredField &&
      Number.isFinite(thresholdNum) &&
      thresholdNum <= maxPossibleScore);

  const isValid =
    name.trim().length > 0 &&
    fields.length > 0 &&
    // A form needs at least one answerable field. Headings and info screens are
    // display-only (never required/scored), so a form of nothing but those would
    // let a learner "complete" it by submitting nothing.
    fields.some((f) => !isDisplayOnlyType(f.type)) &&
    // Every question needs wording. An info screen is display-only, so a
    // headline is optional there as long as it shows something (a message or
    // an image).
    fields.every((f) =>
      f.type === "info_screen"
        ? !!(f.label.trim() || (f.help || "").trim() || f.image_uuid)
        : f.label.trim().length > 0
    ) &&
    scoringConfigValid &&
    // A single select must offer at least one non-empty option, or learners get
    // an unanswerable field and the section can't be completed.
    fields.every(
      (f) =>
        !hasOptions(f.type) ||
        (f.options || []).some((o) => (o.label || "").trim().length > 0)
    ) &&
    // Option labels must be unique within a select — responses store the label,
    // so duplicates are indistinguishable and can record the wrong score.
    fields.every((f) => {
      if (!hasOptions(f.type)) return true;
      const labels = (f.options || [])
        .map((o) => (o.label || "").trim())
        .filter((l) => l.length > 0);
      return new Set(labels).size === labels.length;
    }) &&
    // A display condition must point at an EARLIER answerable field (the
    // backend drops anything else, which would silently un-gate a follow-up).
    showIfProblems.length === 0 &&
    // Piped answers must reference an earlier question (a deleted/moved source
    // would render as a placeholder to learners).
    pipingProblems.length === 0 &&
    // Scored fields can't be conditional (the max reachable score would depend
    // on the branch; the backend rejects the save).
    fields.every((f) => !(f.scored && f.show_if));

  // closeAfter=false keeps the editor open (used by the Share popover's
  // "Save form" so the public link reflects edits without losing the modal).
  const saveForm = async (closeAfter) => {
    if (!isValid || isSaving || loadError) return false;
    setIsSaving(true);
    try {
      // Don't ship stale `scored` flags when scoring is off.
      let fieldsPayload = scoringEnabled
        ? fields
        : fields.map((f) => ({ ...f, scored: false }));
      // Defensive: never ship a graded free-text field on a manager-completed
      // form (the backend rejects it). The role onChange already clears these;
      // this guards any other path into that state.
      if (completedByRole === "manager") {
        fieldsPayload = fieldsPayload.map((f) => {
          if (isManualType(f.type) && f.scored) {
            const { max_points, answer_key, ...rest } = f;
            return { ...rest, scored: false };
          }
          return f;
        });
      }
      const requestData = {
        company_id: props.homeCompany,
        learning_form_uuid: isEdit ? seed.uuid : null,
        name: name.trim(),
        description: description,
        completed_by_role: completedByRole,
        scoring_enabled: scoringEnabled,
        pass_threshold:
          scoringEnabled && passThreshold !== "" ? Number(passThreshold) : null,
        // Only meaningful with scoring + a threshold; the backend also enforces this.
        gate_on_pass:
          scoringEnabled && passThreshold !== "" ? gateOnPass : false,
        save_to_profile: saveToProfile,
        // Which tab owns the form. Sent on every save so a survey created
        // here never lands in Training Content.
        purpose: isEngagement ? "engagement" : "training",
        presentation,
        welcome_screen: JSON.stringify(normalizeScreen(welcomeScreen)),
        thank_you_screen: JSON.stringify(normalizeScreen(thankYouScreen)),
        fields: JSON.stringify(fieldsPayload),
      };
      const res = await postRightworkAPI(API_post_learning_form, requestData);
      if (res.status === 200) {
        const savedForm = res.data.redux?.payload?.learning_form;
        toast.success(isEngagement ? "Survey saved" : "Form saved", { autoClose: 2000 });
        // Persist the "make directly assignable" setting on Save (only when it
        // changed) — like every other form setting.
        if (
          savedForm?.uuid &&
          !!directlyAssignable !== originalAssignableRef.current
        ) {
          await setDirectAssignability({
            companyId: props.homeCompany,
            assetType: "form",
            assetUuid: savedForm.uuid,
            isAssignable: !!directlyAssignable,
          });
          // Keep the baseline current so a second in-place save doesn't redo it.
          originalAssignableRef.current = !!directlyAssignable;
        }
        // Notify the parent immediately only on a CLOSING save. For in-place
        // saves the editor stays open, and callbacks like
        // SectionInputsModal2.handleFormSaved restamp the embedded block + clear
        // the edit-context ref — which would make a later normal Save insert a
        // duplicate block. Instead, QUEUE the saved form and replay it on close
        // (handleCloseWithFlush) so a rename still reaches the section editor.
        if (closeAfter) {
          if (props.onSaveSuccess) props.onSaveSuccess(savedForm);
          pendingSavedFormRef.current = null;
        } else {
          pendingSavedFormRef.current = savedForm;
        }
        // Mark the current content as saved so the unsaved-changes flag clears.
        baselineRef.current = currentSnapshot();
        if (closeAfter) props.handleClose();
        return true;
      }
      return false;
    } catch (error) {
      if (error.response?.data?.system?.message) {
        toast.error(error.response.data.system.message, { autoClose: 3000 });
      } else {
        toast.error(`Failed to save ${noun}`, { autoClose: 3000 });
      }
      console.error("Error saving form:", error);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = () => saveForm(true);
  const handleSaveInPlace = () => saveForm(false);

  // Close, flushing any deferred in-place save to the parent first so a rename /
  // edit made via the Share popover reaches the embedding section editor even
  // when the user closes with X instead of a closing Save.
  const handleCloseWithFlush = () => {
    if (pendingSavedFormRef.current && props.onSaveSuccess) {
      props.onSaveSuccess(pendingSavedFormRef.current);
    }
    pendingSavedFormRef.current = null;
    setConfirmDiscard(false);
    props.handleClose();
  };

  // Work that would be lost by closing: anything Save would persist differs
  // from the snapshot taken when the editor opened (applySeed sets that for
  // new forms too, so description / settings / an unlabeled field all count),
  // plus the separately held assignability toggle.
  const wouldLoseWork =
    !isLoading &&
    !loadError &&
    ((baselineRef.current != null && currentSnapshot() !== baselineRef.current) ||
      !!directlyAssignable !== originalAssignableRef.current);

  const requestClose = () => {
    if (isSaving) return;
    if (wouldLoseWork) setConfirmDiscard(true);
    else handleCloseWithFlush();
  };

  const handleDelete = async () => {
    const formUuid = props.modalDataForm?.uuid;
    if (!formUuid || isDeleting) return;
    setIsDeleting(true);
    try {
      toast.loading(`Deleting ${noun}...`, { autoClose: false });
      await postRightworkAPI(API_delete_learning_form, {
        company_id: props.homeCompany,
        learning_form_uuid: formUuid,
      });
      toast.dismiss();
      toast.success(isEngagement ? "Survey deleted" : "Form deleted", { autoClose: 2000 });
      setConfirmDelete(false);
      // The form is gone — drop any queued in-place save so it isn't replayed.
      pendingSavedFormRef.current = null;
      // onSaveSuccess refreshes the forms list (same hook the save path uses).
      if (props.onSaveSuccess) props.onSaveSuccess();
      props.handleClose();
    } catch (error) {
      toast.dismiss();
      // Surface the backend reason (e.g. "still used in N sections") so the
      // admin knows what to fix, instead of a generic failure.
      const message =
        error?.response?.data?.system?.message || `Failed to delete ${noun}`;
      toast.error(message, { autoClose: 5000 });
    } finally {
      setIsDeleting(false);
    }
  };

  // The in-progress draft, previewed directly (no save/fetch round-trip).
  const draftForm = {
    name: name.trim() || `Untitled ${noun}`,
    description,
    completed_by_role: completedByRole,
    fields: scoringEnabled ? fields : fields.map((f) => ({ ...f, scored: false })),
    scoring_enabled: scoringEnabled,
    pass_threshold:
      scoringEnabled && passThreshold !== "" ? Number(passThreshold) : null,
    gate_on_pass: scoringEnabled && passThreshold !== "" ? gateOnPass : false,
    presentation,
    welcome_screen: normalizeScreen(welcomeScreen),
    thank_you_screen: normalizeScreen(thankYouScreen),
  };

  return (
    <>
    {/* disableEnforceFocus: the slides-mode preview (FormPlayer) is portaled to
        document.body, OUTSIDE this modal's DOM, so MUI's focus trap would pull
        focus back here and make its text inputs untypable. Same rationale as
        FormPreviewModal. */}
    <Modal open={props.open} disableAutoFocus disableEnforceFocus>
      <Box sx={style}>
        {/* = = = Header = = = */}
        <div className="flex items-center gap-3 border-b border-slate-200 bg-white px-4 py-3 shrink-0">
          <div className="w-9 h-9 bg-violet-100 rounded-lg flex items-center justify-center shrink-0">
            <DescriptionOutlinedIcon className="text-violet-600" style={{ fontSize: 20 }} />
          </div>
          <input
            type="text"
            placeholder={
              isEngagement
                ? isEdit ? "Survey name" : "Untitled survey"
                : isEdit ? "Form name" : "Untitled form"
            }
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="flex-1 min-w-0 text-xl font-semibold text-slate-900 bg-transparent border-none focus:outline-none"
          />
          {!isLoading && !loadError && !isEngagement && (
            <ReuseSignalBanner count={sectionUsageCount} />
          )}
          {/* Public link + responses live in the Engagement tab. A training
              form only shows them while it still has links from before the
              split, so those stay manageable (turn off / regenerate) until
              the form is moved; the server refuses NEW links on training
              forms. */}
          {/* Engagement mounts the controls before the first save too (the
              popover says "save first", like the deck editor). */}
          {!isLoading && !loadError && completedByRole === "employee" &&
            (isEngagement || (isEdit && publicShares.length > 0)) && (
            <FormPublicShareControls
              companyId={props.homeCompany}
              formUuid={isEdit ? seed.uuid : null}
              nounLabel={isEngagement ? "survey" : "form"}
              formName={name}
              fields={fields}
              initialShares={publicShares}
              onSharesChange={(next) => {
                setPublicShares(next);
                // The forms list shows this form's link state (Shared badge,
                // sharing filter, response count), and links are toggled here
                // without saving the form, so it needs telling separately from
                // onSaveSuccess.
                if (props.onPublicShareChange) props.onPublicShareChange();
              }}
              hasUnsavedChanges={hasUnsavedChanges}
              canSaveForm={isValid && !loadError && !isDeleting}
              savingForm={isSaving}
              onSaveForm={handleSaveInPlace}
            />
          )}
          {/* Header groups, left to right: what the form collects (Report /
              Responses / Share, shown when applicable), a divider, then
              authoring (Preview, AI), then the primary action. */}
          <div className="w-px h-6 bg-slate-200 mx-1 shrink-0" aria-hidden="true" />
          <button
            type="button"
            onClick={() =>
              presentation === "slides" ? setPlayerPreviewOpen(true) : setPreviewOpen(true)
            }
            disabled={isLoading || loadError}
            className={[
              "inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors shrink-0",
              !isLoading && !loadError
                ? "bg-slate-100 hover:bg-slate-200 text-slate-700"
                : "bg-slate-50 text-slate-300 cursor-not-allowed",
            ].join(" ")}
          >
            <VisibilityIcon style={{ fontSize: 18 }} />
            Preview
          </button>
          <button
            type="button"
            onClick={() => setAiGenOpen(true)}
            disabled={isLoading || loadError}
            className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-violet-700 border border-violet-200 hover:bg-violet-50 transition-colors shrink-0 disabled:opacity-40"
          >
            <AutoAwesomeIcon style={{ fontSize: 18 }} />
            {hasEditableContent ? "Edit with AI" : "Generate with AI"}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!isValid || isSaving || isDeleting || isLoading || loadError}
            className="inline-flex items-center rounded-lg px-4 py-2 text-sm font-semibold text-white bg-violet-700 hover:bg-violet-800 transition-colors disabled:opacity-60 shrink-0"
          >
            {isSaving
              ? "Saving…"
              : isEngagement
              ? isEdit ? "Save Survey" : "Create Survey"
              : isEdit ? "Save Form" : "Create Form"}
          </button>
          <button
            onClick={requestClose}
            disabled={isSaving}
            aria-label="Close"
            title={isSaving ? "Finishing save…" : "Close"}
            className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-700 transition-colors shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CloseIcon />
          </button>
        </div>

        {/* = = = Body: 3 panes (or loading / failed-load state) = = = */}
        <div className="flex flex-1 overflow-hidden min-h-0">
          {loadError ? (
            <div className="flex-1 flex flex-col items-center justify-center bg-white px-6 text-center">
              <p className="text-slate-900 font-semibold">Couldn't load this {noun}</p>
              <p className="text-sm text-slate-500 mt-1">
                It may have been deleted, or there was a network problem. Close and
                try again.
              </p>
            </div>
          ) : isLoading ? (
            <div className="flex-1 flex items-center justify-center bg-white">
              <LoadSpinner />
            </div>
          ) : (
            <>
              {/* - - - Left rail: field list - - - */}
              <FormFieldListPane
                fields={fields}
                selectedIndex={selectedFieldIndex}
                hoveredIndex={hoveredFieldIndex}
                onHover={setHoveredFieldIndex}
                onSelect={handleSelectFieldFromRail}
                onRemove={removeField}
                onDragEnd={handleFieldDragEnd}
                onOpenAddMenu={(anchor) => setAddMenuAnchor(anchor)}
              />

              {/* - - - Center: the whole form (all fields, editable inline) - - - */}
              <div className="flex-1 overflow-y-auto bg-slate-50">
                {fields.length === 0 ? (
                  // Empty state follows the swipe deck's EmptyStoryboard shape
                  // (headline + one norm-setting line + primary CTA), with
                  // Generate with AI as the secondary path.
                  <div className="h-full flex items-center justify-center px-6 text-center">
                    <div className="max-w-md">
                      <div className="text-5xl mb-4">📋</div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">
                        Start with your first field
                      </h3>
                      <p className="text-sm text-slate-500 mb-6">
                        Forms capture rubrics, checklists, sign-offs, and shift
                        notes. Each field is one question or check.
                      </p>
                      <div className="flex items-center justify-center gap-3">
                        <button
                          type="button"
                          onClick={(e) => setAddMenuAnchor(e.currentTarget)}
                          className="inline-flex items-center gap-2 rounded-lg bg-violet-700 hover:bg-violet-800 text-white px-4 py-2 font-medium transition-colors"
                        >
                          <AddIcon style={{ fontSize: 20 }} />
                          Add a field
                        </button>
                        <button
                          type="button"
                          onClick={() => setAiGenOpen(true)}
                          className="inline-flex items-center gap-2 rounded-lg border border-violet-200 text-violet-700 hover:bg-violet-50 px-4 py-2 font-medium transition-colors"
                        >
                          <AutoAwesomeIcon style={{ fontSize: 18 }} />
                          Generate with AI
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="max-w-3xl mx-auto px-8 py-8 space-y-3">
                    {fields.map((field, idx) => (
                      <div
                        key={field.id}
                        ref={(el) => {
                          fieldRefs.current[field.id] = el;
                        }}
                        // Clicking a card focuses it (highlight + rail sync), but
                        // doesn't scroll — it's already in view.
                        onMouseDown={() => setSelectedFieldIndex(idx)}
                        className="scroll-mt-4"
                      >
                        <FormFieldEditorPane
                          field={field}
                          index={idx}
                          isSelected={idx === selectedFieldIndex}
                          scoringEnabled={scoringEnabled && !isEngagement}
                          completedByRole={completedByRole}
                          onUpdate={(patch) => updateField(idx, patch)}
                          onUpdateOption={(optIdx, patch) =>
                            updateOption(idx, optIdx, patch)
                          }
                          onAddOption={() => addOption(idx)}
                          onRemoveOption={(optIdx) => removeOption(idx, optIdx)}
                          autoFocus={field.id === focusFieldId}
                          earlierFields={fields.slice(0, idx)}
                          companyId={props.homeCompany}
                          isEnding={field.id === endingId}
                        />
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={(e) => setAddMenuAnchor(e.currentTarget)}
                      className="w-full bg-violet-50 hover:bg-violet-100 border-2 border-dashed border-violet-200 hover:border-violet-300 rounded-lg px-4 py-2 flex items-center justify-center gap-2 text-violet-700 font-medium"
                    >
                      <AddIcon style={{ fontSize: 20 }} /> Add field
                    </button>
                  </div>
                )}
              </div>

              {/* - - - Right rail: form settings - - - */}
              {showSettings ? (
                <FormSettingsPane
                  isEdit={isEdit}
                  variant={isEngagement ? "engagement" : "training"}
                  description={description}
                  onDescriptionChange={setDescription}
                  completedByRole={completedByRole}
                  onCompletedByRoleChange={handleCompletedByRoleChange}
                  scoringEnabled={scoringEnabled}
                  onScoringChange={setScoringEnabled}
                  passThreshold={passThreshold}
                  onPassThresholdChange={setPassThreshold}
                  gateOnPass={gateOnPass}
                  onGateChange={setGateOnPass}
                  hasScoredField={hasScoredField}
                  thresholdNum={thresholdNum}
                  maxPossibleScore={maxPossibleScore}
                  saveToProfile={saveToProfile}
                  onSaveToProfileChange={setSaveToProfile}
                  directlyAssignable={directlyAssignable}
                  onDirectlyAssignableChange={setDirectlyAssignable}
                  presentation={presentation}
                  onPresentationChange={handlePresentationChange}
                  onClose={() => setShowSettings(false)}
                  onDeleteClick={() => setConfirmDelete(true)}
                  deleteDisabled={isSaving || isDeleting || isLoading || loadError}
                />
              ) : (
                <div className="border-l border-slate-200 bg-white flex items-start py-3 px-1">
                  <Tooltip title="Show settings" placement="left">
                    <IconButton size="small" onClick={() => setShowSettings(true)}>
                      <TuneIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
              )}
            </>
          )}
        </div>

        {/* +Add field type menu (anchored to the rail's Add button) */}
        <Menu
          anchorEl={addMenuAnchor}
          open={Boolean(addMenuAnchor)}
          onClose={() => setAddMenuAnchor(null)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          {typesForPresentation(presentation).map((t) => (
            <MenuItem
              key={t.value}
              onClick={() => handleAddField(t.value)}
              sx={{ fontSize: 14 }}
            >
              {t.label}
            </MenuItem>
          ))}
        </Menu>

        {confirmDiscard && (
          <DiscardChangesConfirm
            noun={isEngagement ? "survey" : "form"}
            isNew={!isEdit}
            onKeepEditing={() => setConfirmDiscard(false)}
            onDiscard={handleCloseWithFlush}
          />
        )}
      </Box>
    </Modal>

    <FormPreviewModal
      open={previewOpen}
      previewForm={draftForm}
      onClose={() => setPreviewOpen(false)}
    />

    {/* Slide-mode preview: the real player, preview mode (no writes). Portaled
        to body like the section block's player, since this modal's Box uses a
        transform. */}
    {playerPreviewOpen &&
      ReactDOM.createPortal(
        <React.Suspense fallback={null}>
          <FormPlayerLazy
            mode="preview"
            form={draftForm}
            onClose={() => setPlayerPreviewOpen(false)}
          />
        </React.Suspense>,
        document.body
      )}

    <AiAssetGeneratorModal
      variant={isEngagement ? "survey" : "form"}
      open={aiGenOpen}
      handleClose={() => setAiGenOpen(false)}
      homeCompany={props.homeCompany}
      sectionsData={props.sectionsData || []}
      modulesData={props.modulesData || []}
      requestParams={isEngagement ? { purpose: "engagement" } : undefined}
      onGenerated={seedFromAiDraft}
      // Current editor state, so the modal can offer "edit with AI" (a
      // targeted revision) once there's real content. Field ids ride along —
      // form field ids are durable through save, so kept fields come back
      // with their ids intact and responses/grading stay keyed correctly.
      currentAsset={
        hasEditableContent
          ? {
              name: name.trim(),
              description,
              completed_by_role: completedByRole,
              scoring_enabled: scoringEnabled,
              pass_threshold:
                scoringEnabled && passThreshold !== ""
                  ? Number(passThreshold)
                  : null,
              // So an edit knows whether it's revising a scrolling form or a
              // slide show (which questions become screens, and where info
              // screens belong).
              presentation,
              fields,
            }
          : null
      }
    />

    {/* Delete confirmation, layered over the editor */}
    <Modal open={confirmDelete} onClose={() => !isDeleting && setConfirmDelete(false)}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "100%",
          maxWidth: 440,
          bgcolor: "background.paper",
          borderRadius: "16px",
          boxShadow: 24,
          p: 3,
          outline: "none",
        }}
      >
        <h3 className="text-lg font-semibold text-slate-900 mb-1">
          {isEngagement ? "Delete this survey?" : "Delete this form?"}
        </h3>
        <p className="text-sm text-slate-500 mb-6">
          {isEngagement
            ? `"${name.trim() || "Untitled survey"}" and every response collected through its links will be permanently removed.`
            : `"${name.trim() || "Untitled form"}" will be permanently removed. Sections that reference it will no longer be completable until the reference is removed.`}
        </p>
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => setConfirmDelete(false)}
            disabled={isDeleting}
            className="px-4 py-2 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg text-sm font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {isDeleting && <LoadSpinner size={16} />}
            Delete form
          </button>
        </div>
      </Box>
    </Modal>
    </>
  );
}

export default FormEditorModal;
